import { PublicationError } from '../publication';

/** Detached semantic input: shared by editor staging and worker planning.
 * No Y.Doc, editor model, storage, UI effect or mutable input crosses this port.
 */
export interface DatabaseIntentRow {
  rowId: string;
  cells: Record<string, { columnId: string; value: unknown }>;
}

export function applyDatabaseRowIntent(
  before: readonly DatabaseIntentRow[],
  after: readonly DatabaseIntentRow[],
  basis: readonly DatabaseIntentRow[]
): DatabaseIntentRow[] {
  const next = structuredClone([...basis]);
  const old = new Map(before.map(row => [row.rowId, row]));
  const wanted = new Map(after.map(row => [row.rowId, row]));
  for (let index = next.length - 1; index >= 0; index--) {
    if (old.has(next[index].rowId) && !wanted.has(next[index].rowId))
      next.splice(index, 1);
  }
  // Preserve the first occurrence for the explicit duplicate-order repair below.
  // One index avoids scanning the whole shard again for each changed row.
  const currentRows = new Map<string, DatabaseIntentRow>();
  for (const row of next)
    if (!currentRows.has(row.rowId)) currentRows.set(row.rowId, row);
  for (const row of after) {
    const previous = old.get(row.rowId);
    let current = currentRows.get(row.rowId);
    if (!previous) {
      if (current) throw new PublicationError('LOCAL_CONFLICT');
      current = structuredClone(row);
      next.push(current);
      currentRows.set(row.rowId, current);
    } else {
      if (!current) throw new PublicationError('LOCAL_CONFLICT');
      for (const key of new Set([
        ...Object.keys(previous.cells),
        ...Object.keys(row.cells),
      ])) {
        if (
          JSON.stringify(previous.cells[key]) === JSON.stringify(row.cells[key])
        )
          continue;
        if (key in row.cells)
          current.cells[key] = structuredClone(row.cells[key]);
        else delete current.cells[key];
      }
    }
  }
  // A repair may explicitly remove repeated order entries for an addressed row.
  const seen = new Set<string>();
  for (let index = 0; index < next.length; index++) {
    const id = next[index].rowId;
    if (!wanted.has(id)) continue;
    if (seen.has(id)) next.splice(index--, 1);
    else seen.add(id);
  }
  if (
    before.map(row => row.rowId).join('\0') !==
    after.map(row => row.rowId).join('\0')
  ) {
    const order = new Map(after.map((row, index) => [row.rowId, index]));
    next.sort(
      (a, b) =>
        (order.get(a.rowId) ?? Infinity) - (order.get(b.rowId) ?? Infinity)
    );
  }
  return next;
}
